import { decode, getBreakpoint } from 'utils/general';
import select from 'dom-select';
import inViewport from 'in-viewport';
import on, { off } from 'dom-event';
import debounce from 'lodash.debounce';
import { listProductsEventData } from 'lib/tracking';
import datalayer from 'lib/datalayer';
import { A11y, Navigation, Keyboard } from 'swiper/modules';

/**
 * Product card slider swiper component
 */
export default {
  components: {
    'product-card': () => import('../product-card/product-card.vue'),
    'vue-swiper': () => import('../vue-swiper/vue-swiper.vue'),
  },
  props: {
    products: { default: () => [] },
    dynamicTitle: { type: String, default: '' },
    parentTitle: { type: String, default: '' },
    listType: { type: String, default: '' },
    swiperOptions: {
      a11y: {
        enabled: true,
      },
      keyboard: true,
      autoHeight: true,
      modules: [A11y, Navigation, Keyboard],
      navigation: {
        nextEl: '.slider-next',
        prevEl: '.slider-prev',
      }
    },
    handleResize: { type: Boolean, default: true },
    isSlider: { type: Boolean, default: true },
    showProgressBar: { type: Boolean, default: false },
    showControls: { type: Boolean, default: true },
    disableOnMobile: { type: Boolean, default: false },
    stepKey: { type: Number, default: 0 },
    variants: { type: String, default: '' }
  },
  data() {
    return {
      isViewed: false,
      viewportSize: '',
      progress: 0,
      currentSlideIndex: 0,
      totalSlides: 0
    };
  },
  watch: {
    productsDecoded(_new, _old) {
      if (this.isViewed) {
        this.trackView();
      }
    }
  },
  mounted() {
    setTimeout(() => {
      this.resizeHandler();
    }, 250);
    on(window, 'resize', debounce(this.resizeHandler, 300));
    inViewport(this.$el, () => {
      this.isViewed = true;
      this.trackView();
    });

    this.$nextTick(() => {
      this.updateSlideAccessibility();
      this.setupSwiperEventListeners();
      this.updateScreenReaderAnnouncement();
    })
  },
  destroyed() {
    off(window, 'resize', this.resizeHandler);
  },
  computed: {
    productsDecoded() {
      const productsData =
        typeof this.products === 'string'
          ? JSON.parse(decode(this.products))
          : this.products;

      return productsData.map((p, index) => ({ ...p, index: index }));
    },
    variantsDecoded() {
      if (this.variants) {
        return this.variants.split(',')
      }

      return []
    }
  },
  methods: {
    resizeHandler() {
      if (!this.handleResize) return;

      this.viewportSize = getBreakpoint();

      if (this.viewportSize === 'xs' || this.viewportSize === 'xxs') {
        this.adjustVTO();
      }
    },
    adjustVTO() {
      // eslint-disable-next-line no-case-declarations
      const vtoItems = select(
        '.foundation-vto__slider .product-card__featured',
      );
      const vtoPageDots = select('.foundation-vto__slider .flickity-page-dots');
      if (vtoItems && vtoPageDots) {
        const topPos = vtoItems.offsetHeight + 8;
        vtoPageDots.style.top = `${topPos}px`;
      }
    },
    async trackView() {
      if (this.productsDecoded.length < 1) {
        return;
      }

      const extraData = {
        listName: this.parentTitle,
        listId: this.listType,
        origin: document.body.dataset.origin,
        originProduct: document.body.dataset.originProduct,
      };

      const { products, hasBundleBuilder } = await listProductsEventData(
        this.productsDecoded,
        extraData,
      );

      let eventData = {
        ecommerce: {
          item_list_id: this.listType || undefined,
          item_list_name: this.parentTitle || undefined,
          items: products,
        },
      };
      if (hasBundleBuilder) {
        eventData = {
          ...eventData,
          site_section: window.__GLOBAL__.siteSection,
          component_name: 'Bundle Builder PLP Product Card',
          bundle_type: 'bundle builder',
        };
      }
      // track visible elements on page load
      datalayer.dataLayerPush('view_item_list', eventData);
    },
    onSelectItem(value) {
      this.$emit('select-item', {
        key: Number(this.stepKey),
        value,
      });
    },
    setupSwiperEventListeners() {
      if (!this.$refs.carousel || !this.$refs.carousel.$swiper) {
        setTimeout(() => this.setupSwiperEventListeners(), 100);
        return;
      }

      const swiper = this.$refs.carousel.$swiper;

      swiper.on('slideChange', () => {
        this.currentSlideIndex = swiper.activeIndex;
        this.updateSlideAccessibility();
        this.updateScreenReaderAnnouncement();
      });

      swiper.on('resize', () => {
        this.updateSlideAccessibility();
      });

      // Initialize slide count
      this.totalSlides = swiper.slides.length;
      this.currentSlideIndex = swiper.activeIndex;
    },
    updateSlideAccessibility() {
      if (!this.$refs.carousel || !this.$refs.carousel.$swiper) return;

      const swiper = this.$refs.carousel.$swiper;
      const multiView = swiper.params.slidesPerView !== 1 && swiper.params.slidesPerView !== 'auto';

      swiper.slides.forEach((slide, index) => {
        const isVisible = multiView
          ? slide.classList.contains('swiper-slide-visible')
          : index === swiper.activeIndex;

        if (isVisible) {
          slide.setAttribute('aria-hidden', 'false');
          slide.setAttribute('tabindex', '0');

          // Remove tabindex from focusable elements to restore natural tab order
          const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex], .product-card__featured, .product-card__swatches');
          focusableElements.forEach((element) => {
            element.removeAttribute('tabindex');
          });
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('tabindex', '-1');

          // Add tabindex="-1" to each focusable element within non-visible slide container
          const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, .product-card__featured, .product-card__swatches');
          focusableElements.forEach((element) => {
            element.setAttribute('tabindex', '-1');
          });
        }
      });
    },
    updateScreenReaderAnnouncement() {
      if (!this.$refs.carouselDescription) return;

      const slideNumber = this.currentSlideIndex + 1;
      const announcement = `Slide ${slideNumber} of ${this.totalSlides}. Use arrow keys to navigate through products.`;

      this.$refs.carouselDescription.textContent = announcement;
    }
  },
};
